(() => {
  if (window.__arcticmedCartLineMerge) return;
  window.__arcticmedCartLineMerge = true;

  let itemsObserver = null;
  let observedItems = null;
  let mergeTimer = null;
  let merging = false;

  const rootUrl = () => window.Shopify?.routes?.root || '/';

  const formatPrice = (cents, currency) => {
    const value = Number(cents) / 100;
    const fractionDigits = Number.isInteger(value) ? 0 : 2;

    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: currency || 'SEK',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: 2
    }).format(value);
  };

  const itemSignature = (item) => {
    const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id || '';
    const properties = item.properties || {};
    const normalizedProperties = Object.keys(properties)
      .sort()
      .map((key) => [key, properties[key]]);

    return `${item.variant_id}|${sellingPlanId}|${JSON.stringify(normalizedProperties)}`;
  };

  const fetchCart = async () => {
    const response = await fetch(`${rootUrl()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error('Varukorgen kunde inte läsas.');
    return response.json();
  };

  const mergeRows = async (cart) => {
    if (merging) return;
    const itemsWrap = document.querySelector('[data-wubs-cd-items]');
    if (!itemsWrap) return;

    merging = true;

    try {
      const currentCart = cart || await fetchCart();
      const rows = new Map();

      itemsWrap.querySelectorAll('[data-wubs-item]').forEach((row) => {
        row.hidden = false;
        row.removeAttribute('data-arcticmed-merged-lines');
        row.removeAttribute('data-arcticmed-merged-quantity');
        rows.set(row.getAttribute('data-key'), row);
      });

      const groups = new Map();
      currentCart.items.forEach((item) => {
        const signature = itemSignature(item);
        if (!groups.has(signature)) groups.set(signature, []);
        groups.get(signature).push(item);
      });

      groups.forEach((group) => {
        if (group.length < 2) return;

        const primaryRow = rows.get(group[0].key);
        if (!primaryRow) return;

        const quantity = group.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        const finalLinePrice = group.reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
        const lines = group.map((item) => ({ key: item.key, quantity: Number(item.quantity || 0) }));

        primaryRow.setAttribute('data-arcticmed-merged-lines', JSON.stringify(lines));
        primaryRow.setAttribute('data-arcticmed-merged-quantity', String(quantity));

        const quantityNode = primaryRow.querySelector('[data-wubs-qty-num]');
        const priceNode = primaryRow.querySelector('[data-wubs-line-price]');
        if (quantityNode) quantityNode.textContent = String(quantity);
        if (priceNode) priceNode.textContent = formatPrice(finalLinePrice, currentCart.currency);

        group.slice(1).forEach((item) => {
          const duplicateRow = rows.get(item.key);
          if (duplicateRow) duplicateRow.hidden = true;
        });
      });
    } finally {
      merging = false;
    }
  };

  const scheduleMerge = (cart) => {
    window.clearTimeout(mergeTimer);
    mergeTimer = window.setTimeout(() => mergeRows(cart), 20);
  };

  const observeItems = () => {
    const items = document.querySelector('[data-wubs-cd-items]');
    if (!items || items === observedItems) return;

    itemsObserver?.disconnect();
    observedItems = items;
    itemsObserver = new MutationObserver(() => scheduleMerge());
    itemsObserver.observe(items, { childList: true });
    scheduleMerge();
  };

  const updateMergedLines = async (row, nextQuantity) => {
    const lines = JSON.parse(row.getAttribute('data-arcticmed-merged-lines') || '[]');
    if (!lines.length) return;

    const updates = {};
    lines.forEach((line, index) => {
      updates[line.key] = index === 0 ? nextQuantity : 0;
    });

    row.dataset.arcticmedMergedBusy = 'true';
    row.style.opacity = '0.55';
    row.style.pointerEvents = 'none';

    try {
      const response = await fetch(`${rootUrl()}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Varukorgen kunde inte uppdateras.');

      if (typeof window.wubsCartDrawerRefresh === 'function') {
        await window.wubsCartDrawerRefresh();
      } else {
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
        await mergeRows(cart);
      }
    } finally {
      row.removeAttribute('data-arcticmed-merged-busy');
      row.style.opacity = '';
      row.style.pointerEvents = '';
    }
  };

  document.addEventListener('click', async (event) => {
    const control = event.target.closest('[data-wubs-qty-minus], [data-wubs-qty-plus], [data-wubs-remove]');
    const row = control?.closest('[data-arcticmed-merged-lines]');
    if (!control || !row || row.dataset.arcticmedMergedBusy === 'true') return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const currentQuantity = Number(row.getAttribute('data-arcticmed-merged-quantity')) || 1;
    let nextQuantity = currentQuantity;
    if (control.matches('[data-wubs-qty-minus]')) nextQuantity = Math.max(0, currentQuantity - 1);
    if (control.matches('[data-wubs-qty-plus]')) nextQuantity = currentQuantity + 1;
    if (control.matches('[data-wubs-remove]')) nextQuantity = 0;

    try {
      await updateMergedLines(row, nextQuantity);
    } catch (error) {
      scheduleMerge();
    }
  }, true);

  new MutationObserver(observeItems).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('cart:updated', (event) => scheduleMerge(event.detail?.cart));
  document.addEventListener('cart:refresh', () => scheduleMerge());
  document.addEventListener('wubs:cart-updated', () => scheduleMerge());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeItems, { once: true });
  } else {
    observeItems();
  }
})();
